import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminTransactions } from '../../Hooks/Admin/useAdminTransactions';
import TransactionTable from '../../Components/Admin/TransactionTable';
import FilterInput from '../../Components/FilterInput';
import { FaSearch, FaChevronDown, FaTrash, FaSync } from 'react-icons/fa';

import { statusFilters, operationTypes } from '../../Data/servicesFilters';
import Modal from '../../Components/Modal';
import Alert from '../../Components/Alert';
import { retrySyriatelTransaction, retryMtnTransaction } from '../../API/ProtectedAuth';
import Pagination from '../../Components/Pagination';

const AdminTransactionsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const {
        activeTab,
        transactions,
        total,
        loading,
        error,
        filters,
        setActiveTab,
        setFilters,
        setPage,
        refresh
    } = useAdminTransactions();

    // Sync URL → hook page on first render
    useEffect(() => {
        const urlPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
        if (urlPage !== 1) setPage(urlPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePageChange = (newPage) => {
        setPage(newPage);
        setSearchParams(prev => { prev.set('page', String(newPage)); return prev; }, { replace: true });
    };

    const resetUrlPage = () =>
        setSearchParams(prev => { prev.set('page', '1'); return prev; }, { replace: true });

    const [ordering, setOrdering] = useState('-created_at');
    const [localSearch, setLocalSearch] = useState('');
    const [operationDropdownOpen, setOperationDropdownOpen] = useState(false);
    const [pageSizeDropdownOpen, setPageSizeDropdownOpen] = useState(false);
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('all');

    // Retry Transaction State
    const [retryModalOpen, setRetryModalOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [alertData, setAlertData] = useState(null); // { type: 'success'|'error', message: string }
    const [actionLoading, setActionLoading] = useState(false);

    const handleStatusFilter = (statusValue) => {
        setSelectedStatus(statusValue);
        let newFilters = { page: 1 };

        if (statusValue === 'all') {
            newFilters.api_status = '';
            newFilters.is_debt = undefined;
        } else if (statusValue === 'debt') {
            newFilters.api_status = '';
            newFilters.is_debt = true;
        } else if (statusValue === 'paid') {
            newFilters.api_status = '';
            newFilters.is_debt = false;
        } else {
            newFilters.api_status = statusValue;
            newFilters.is_debt = undefined;
        }
        setFilters(newFilters);
        resetUrlPage();
    };

    const handleClearFilters = () => {
        setFilters({
            search: '',
            created_at: '',
            date_from: '',
            date_to: '',
            operation_type: '',
            api_status: '',
            is_debt: undefined,
            page: 1,
            page_size: filters.page_size // Preserve current page size
        });
        setLocalSearch('');
        setSelectedStatus('all');
        resetUrlPage();
    };

    const handleSort = (field, direction = 'asc') => {
        setOrdering(direction === 'asc' ? field : `-${field}`);
        // You might need to pass ordering to hook if API supports it
    };

    const handleSearch = () => {
        setFilters({ search: localSearch });
    };

    const handleAction = (transaction) => {
        setSelectedTransaction(transaction);
        setRetryModalOpen(true);
    };

    const handleConfirmRetry = async () => {
        if (!selectedTransaction) return;

        setActionLoading(true);
        setRetryModalOpen(false);
        setAlertData(null);

        try {
            const apiCall = activeTab === 'syriatel'
                ? retrySyriatelTransaction
                : retryMtnTransaction;

            await apiCall(selectedTransaction.id);

            setAlertData({
                type: 'success',
                message: 'تم إرسال طلب إعادة المعالجة بنجاح'
            });
            refresh(); // Refresh list to show updated status
        } catch (err) {
            setAlertData({
                type: 'error',
                message: err.response?.data?.message || 'فشل في إعادة معالجة العملية'
            });
        } finally {
            setActionLoading(false);
            setSelectedTransaction(null);
        }
    };



    return (
        <div className="p-6 bg-light min-h-screen font-cairo dir-rtl">
            <div className="flex items-center gap-4 mb-6">
                <h2 className="text-2xl font-bold text-main1">عمليات التحويل لكافة المستخدمين</h2>
                <button
                    onClick={refresh}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-main1 text-white hover:bg-opacity-90 transition-all shadow-sm"
                >
                    <FaSync className={loading ? "animate-spin" : ""} />
                    <span>تحديث البيانات</span>
                </button>
            </div>

            {/* Tabs */}
            <div className="flex space-x-4 mb-6 border-b border-gray-300 pb-2">
                <button
                    onClick={() => {
                        setActiveTab('all');
                        handleClearFilters();
                        resetUrlPage();
                    }}
                    className={`pb-2 px-4 text-lg font-medium transition-colors duration-200 ${activeTab === 'all'
                        ? 'border-b-4 border-main1 text-main1'
                        : 'text-gray-500 hover:text-main1'
                        }`}
                >
                    كافة العمليات
                </button>
                <button
                    onClick={() => {
                        setActiveTab('syriatel');
                        handleClearFilters();
                        resetUrlPage();
                    }}
                    className={`pb-2 px-4 text-lg font-medium transition-colors duration-200 ${activeTab === 'syriatel'
                        ? 'border-b-4 border-main1 text-main1'
                        : 'text-gray-500 hover:text-main1'
                        }`}
                >
                    عمليات Syriatel
                </button>
                <button
                    onClick={() => {
                        setActiveTab('mtn');
                        handleClearFilters();
                        resetUrlPage();
                    }}
                    className={`pb-2 px-4 text-lg font-medium transition-colors duration-200 ${activeTab === 'mtn'
                        ? 'border-b-4 border-main1 text-main1'
                        : 'text-gray-500 hover:text-main1'
                        }`}
                >
                    عمليات MTN
                </button>
            </div>

            {/* Search Box - Reverted to old style but placed above filters */}
            <div className="w-full xl:w-1/3 relative mb-4">
                <FilterInput
                    type="text"
                    placeholder="شريط البحث ....."
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="w-full pl-10 pr-4 shadow-sm border-gray-200"
                />
                <button
                    onClick={handleSearch}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-main1"
                >
                    <FaSearch />
                </button>
            </div>

            {/* Alert */}
            {alertData && (
                <Alert
                    type={alertData.type}
                    onClose={() => setAlertData(null)}
                    className="mb-4"
                >
                    {alertData.message}
                </Alert>
            )}

            {/* الفلاتر */}
            <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4 mb-5">
                <div className="flex flex-wrap gap-2 items-center">
                    {/* Status Dropdown */}
                    <div
                        className="relative"
                        onMouseEnter={() => setStatusDropdownOpen(true)}
                        onMouseLeave={() => setStatusDropdownOpen(false)}
                    >
                        <button
                            className="flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all duration-150 bg-grayish text-main1 hover:bg-main1/10"
                        >
                            {selectedStatus !== 'all'
                                ? statusFilters.find(s => s.value === selectedStatus)?.label
                                : 'كافة الحالات'}
                            <FaChevronDown className={`transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {statusDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-20">
                                {statusFilters
                                    .filter(s => !['debt', 'paid'].includes(s.value))
                                    .map((status) => (
                                        <div
                                            key={status.value}
                                            onClick={() => {
                                                handleStatusFilter(status.value);
                                                setStatusDropdownOpen(false);
                                            }}
                                            className={`px-4 py-2 text-sm cursor-pointer transition text-right
                                            ${selectedStatus === status.value
                                                    ? 'bg-main1 text-white'
                                                    : 'hover:bg-grayish/50 text-main1'
                                                }`}
                                        >
                                            {status.label}
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>

                    {/* Operation Type Dropdown */}
                    <div
                        className="relative"
                        onMouseEnter={() => setOperationDropdownOpen(true)}
                        onMouseLeave={() => setOperationDropdownOpen(false)}
                    >
                        <button
                            className="flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all duration-150 bg-grayish text-main1 hover:bg-main1/10"
                        >
                            {filters.operation_type
                                ? operationTypes.find(t => t.value === filters.operation_type)?.label
                                : 'نوع العملية'}
                            <FaChevronDown className={`transition-transform ${operationDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {operationDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-20">
                                {operationTypes.map((type) => (
                                    <div
                                        key={type.value}
                                        onClick={() => {
                                            setFilters({ operation_type: type.value === 'all' ? '' : type.value });
                                            setOperationDropdownOpen(false);
                                        }}
                                        className={`px-4 py-2 text-sm cursor-pointer transition text-right
                                            ${filters.operation_type === type.value || (type.value === 'all' && !filters.operation_type)
                                                ? 'bg-main1 text-white'
                                                : 'hover:bg-grayish/50 text-main1'
                                            }`}
                                    >
                                        {type.label}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Date Filters + Clear */}
                <div className="flex items-center gap-2">
                    {/* Custom Page Size Dropdown */}
                    <div
                        className="relative ml-4"
                        onMouseEnter={() => setPageSizeDropdownOpen(true)}
                        onMouseLeave={() => setPageSizeDropdownOpen(false)}
                    >
                        <button
                            className="flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all duration-150 bg-grayish text-main1 hover:bg-main1/10"
                        >
                            <span className="text-gray-500 whitespace-nowrap">حجم الصفحة:</span>
                            <span>{filters.page_size}</span>
                            <FaChevronDown className={`transition-transform ${pageSizeDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {pageSizeDropdownOpen && (
                            <div className="absolute left-0 mt-2 w-32 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-20">
                                {[10, 20, 50, 100].map((size) => (
                                    <div
                                        key={size}
                                        onClick={() => {
                                            setFilters({ page_size: size, page: 1 });
                                            setPageSizeDropdownOpen(false);
                                        }}
                                        className={`px-4 py-2 text-sm cursor-pointer transition text-center
                                            ${filters.page_size === size
                                                ? 'bg-main1 text-white'
                                                : 'hover:bg-grayish/50 text-main1'
                                            }`}
                                    >
                                        {size}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <FilterInput
                        label="من"
                        type="date"
                        value={filters.date_from || ''}
                        onChange={(e) => setFilters({ date_from: e.target.value })}
                        className="w-auto"
                    />
                    <FilterInput
                        label="إلى"
                        type="date"
                        value={filters.date_to || ''}
                        onChange={(e) => setFilters({ date_to: e.target.value })}
                        className="w-auto"
                    />

                    <button
                        onClick={handleClearFilters}
                        className="flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-red-50 text-red-600 border border-red-100 hover:bg-red-600 hover:text-white transition-all duration-300 shadow-sm h-[42px] text-sm font-medium"
                    >
                        <FaTrash className="text-sm" />
                        <span>مسح الفلاتر</span>
                    </button>
                </div>
            </div>

            {/* Table */}
            <TransactionTable
                transactions={transactions}
                loading={loading}
                error={error}
                ordering={ordering}
                onSort={handleSort}
                onAction={handleAction}
                showProvider={activeTab === 'all'}
            />

            {/* Pagination */}
            <Pagination
                currentPage={filters.page}
                totalPages={Math.ceil(total / filters.page_size)}
                onPageChange={handlePageChange}
                totalItems={total}
                itemsPerPage={filters.page_size}
                loading={loading}
            />

            {/* Retry Confirmation Modal */}
            <Modal
                isOpen={retryModalOpen}
                onClose={() => setRetryModalOpen(false)}
                title="تأكيد إعادة المعالجة"
            >
                <div>
                    <p className="text-gray-600 mb-6">
                        هل أنت متأكد من رغبتك في إعادة معالجة العملية رقم
                        <span className="font-bold text-main1 mx-1">{selectedTransaction?.id}</span>
                        ؟
                    </p>
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => setRetryModalOpen(false)}
                            className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition"
                        >
                            إلغاء
                        </button>
                        <button
                            onClick={handleConfirmRetry}
                            disabled={actionLoading}
                            className="bg-main1 text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition disabled:opacity-50"
                        >
                            {actionLoading ? 'جاري المعالجة...' : 'تأكيد'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AdminTransactionsPage;
